import React, { useState, useEffect, useRef } from "react";
import { Controller } from "react-hook-form";
import { COUNTRY_CONFIG, DEFAULT_COUNTRY } from "../../../constants/country-config";

export const PhoneInput = ({
  form,
  disabled,
  name = "phoneNumber",
  handleChange = () => {},
  isEditEnabled,
  handleEditNumberClick,
  onCountryChange = () => {}
}) => {
  const {
    control,
    formState: { errors },
    setValue,
    getValues
  } = form;

  const [selectedCountry, setSelectedCountry] = useState(DEFAULT_COUNTRY);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    setValue(name, "", { shouldValidate: false });
    if (inputRef.current) {
      inputRef.current.focus();
    }
    onCountryChange(country);
  };

  const onChangeHandler = (onChange) => (event) => {
    let rawValue = event.target.value;
    let cleanedValue = rawValue.replace(/\D/g, "");

    if (cleanedValue.length > selectedCountry.maxLength) {
      cleanedValue = cleanedValue.slice(0, selectedCountry.maxLength);
    }

    if (event.target.value !== cleanedValue) {
      event.target.value = cleanedValue;
    }

    const syntheticEvent = {
      ...event,
      target: {
        ...event.target,
        value: cleanedValue,
        countryCode: selectedCountry.isdCode,
        country: selectedCountry
      }
    };

    onChange(syntheticEvent);
    handleChange(cleanedValue, selectedCountry);
  };

  function restrictNonNumericalCharacters(event) {
    const input = event.target;
    let value = input.value;

    if (/\D/g.test(value)) {
      value = value.replace(/\D/g, "");
    }

    if (value.length > selectedCountry.maxLength) {
      value = value.slice(0, selectedCountry.maxLength);
    }

    if (input.value !== value) {
      input.value = value;
      const inputEvent = new Event("input", { bubbles: true });
      input.dispatchEvent(inputEvent);
    }
  }

  const handleInputPaste = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text/plain");
    let sanitizedText = pastedText.replace(/\D/g, "");
    sanitizedText = sanitizedText.slice(0, selectedCountry.maxLength);
    document.execCommand("insertText", false, sanitizedText);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div style={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%" }}>
      <div style={{ position: "relative", flex: 1 }} ref={dropdownRef}>
        <Controller
          control={control}
          name={name}
          render={({ field: { ref, onChange, ...field } }) => (
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <div
                className="country-code-selector"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled && !isEditEnabled) {
                    setIsDropdownOpen(!isDropdownOpen);
                  }
                }}
                style={{
                  position: "absolute",
                  left: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: disabled || isEditEnabled ? "not-allowed" : "pointer",
                  opacity: disabled || isEditEnabled ? 0.6 : 1,
                  zIndex: 2,
                  background: "transparent",
                  padding: "4px 8px 4px 0",
                  borderRight: "1px solid #ccc",
                  pointerEvents: disabled || isEditEnabled ? "none" : "auto"
                }}
              >
                <img
                  src={selectedCountry.flagImage}
                  alt={selectedCountry.name}
                  style={{
                    width: "24px",
                    height: "18px",
                    objectFit: "cover",
                    borderRadius: "2px"
                  }}
                />
                <span style={{ color: "#fff", fontSize: "14px", fontWeight: "400", width: "30px" }}>
                  {selectedCountry.isdCode}
                </span>
                <span style={{ color: "#fff", fontSize: "10px", marginLeft: "2px" }}>▼</span>
              </div>

              <input
                {...field}
                ref={(e) => {
                  ref(e);
                  inputRef.current = e;
                }}
                type="tel"
                className={`member--inputbox phoneinput ${errors[name] ? "error--strike" : "input-highlight"}`}
                onChange={onChangeHandler(onChange)}
                style={{
                  fontFamily: "montserrat",
                  width: "100%",
                  paddingLeft: "100px",
                  paddingRight: "12px"
                }}
                disabled={disabled || isEditEnabled}
                maxLength={selectedCountry.maxLength}
                onInput={restrictNonNumericalCharacters}
                required
                onPaste={handleInputPaste}
                placeholder={`Enter ${selectedCountry.maxLength} digit number`}
              />
            </div>
          )}
        />

        {isDropdownOpen && (
          <div
            className="country-dropdown-list"
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              maxHeight: "250px",
              overflowY: "auto",
              background: "white",
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              zIndex: 1000,
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              backgroundColor: "#ffffff"
            }}
          >
            {COUNTRY_CONFIG.map((country) => (
              <div
                key={country.code}
                className="country-option"
                onClick={() => handleCountrySelect(country)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "12px 16px",
                  cursor: "pointer",
                  backgroundColor: selectedCountry.code === country.code ? "#f5f5f5" : "white",
                  borderBottom: "1px solid #f0f0f0",
                  transition: "background-color 0.2s"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9f9f9";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = selectedCountry.code === country.code ? "#f5f5f5" : "white";
                }}
              >
                <img
                  src={country.flagImage}
                  alt={country.name}
                  style={{
                    width: "28px",
                    height: "20px",
                    marginRight: "12px",
                    objectFit: "cover",
                    borderRadius: "2px"
                  }}
                />
                <span
                  style={{
                    marginRight: "12px",
                    fontWeight: "500",
                    color: "#333",
                    minWidth: "60px"
                  }}
                >
                  {country.isdCode}
                </span>
                <span
                  style={{
                    fontSize: "14px",
                    color: "#666",
                    flex: 1
                  }}
                >
                  {country.name}
                </span>
                {selectedCountry.code === country.code && (
                  <span style={{ color: "#4CAF50", marginLeft: "8px" }}>✓</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {isEditEnabled && (
        <span
          className="edit_number"
          onClick={handleEditNumberClick}
          style={{
            marginLeft: "12px",
            cursor: "pointer",
            whiteSpace: "nowrap",
            fontSize: "14px"
          }}
        >
          Edit Number
        </span>
      )}
    </div>
  );
};
